<template>
  <div class="block mx-auto my-48 max-w-448">
    <SitterAlertBanner v-if="serverError" :error="serverError" />
    <PoppinsCard>
      <FormHeader />
      <form id="sitter-sign-up-form" novalidate @submit.prevent="validateAll">
        <PoppinsAccountCreateFields
          ref="accountCreateFields"
          :requiredFields="requiredFields"
          :serverError="serverError"
          @input="handleAccountCreateInput"
          @resetServerError="resetServerError"
        />
        <PhoneField ref="phoneField" @input="handleInput" />
        <BirthDateFields ref="birthDateFields" @input="handleInput" />
        <GenderRadio ref="genderRadio" @input="handleInput" />
        <PoppinsTermsOfUse
          ref="termsOfUse"
          class="mb-24"
          terms-of-use-link="https://www.sittercity.com/terms"
          privacy-policy-link="https://www.sittercity.com/privacy"
          @input="handleInput"
        />
        <PoppinsButton
          id="formSubmit"
          :loading="loading"
          class="w-full"
          variant="primary"
          :value="'Submit'"
          size="medium"
          type="submit"
        />
      </form>
    </PoppinsCard>
    <PoppinsTrustAndSafety />
  </div>
</template>

<script lang="ts">
import { sendSegmentEvent, sendSegmentEventWithTimeout } from '@/lib/segment';
import { writeDomainCookie, readDomainCookie } from '@/lib/domain_cookie';
import { generateSha1Hash } from '@/lib/crypto';
import { CREATE_SITTER } from '@/graphql/account/mutations';
import { ProviderFormData, RequiredFields } from './__types__/SitterCreateForm';
import PhoneField from './components/PhoneField.vue';
import BirthDateFields from './components/BirthDateFields.vue';
import GenderRadio from './components/GenderRadio.vue';
import SitterAlertBanner from './components/SitterAlertBanner.vue';
import SitterCreateFormHeader from './components/SitterCreateFormHeader.vue';
// Poppins components
import Card from '@/components/card/Card.vue';
import PoppinsAccountCreateFields from '@/components/accountCreateFields/AccountCreateFields.vue';
import PoppinsTermsOfUse from '@/components/termsOfUse/PoppinsTermsOfUse.vue';
import PoppinsTrustAndSafety from '@/components/trustAndSafety/PoppinsTrustAndSafety.vue';
import Button from '@/components/button/Button.vue';
import { consumeInvitedSitterToken } from '@/helpers/backupCareHelper';

declare let google: any;

export default {
  name: 'SitterCreateForm',
  components: {
    PoppinsAccountCreateFields,
    PoppinsButton: Button,
    PoppinsCard: Card,
    PoppinsTermsOfUse,
    PoppinsTrustAndSafety,
    FormHeader: SitterCreateFormHeader,
    SitterAlertBanner,
    BirthDateFields,
    PhoneField,
    GenderRadio,
  },
  data(): {
    form: ProviderFormData;
    requiredFields: RequiredFields;
    serverError: Error | null;
    loading: Boolean;
  } {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        location: {
          x: null,
          y: null,
        },
        phoneNumber: '',
        birthDate: '',
        gender: '',
        termsAccepted: false,
      },
      city: null,
      zipCode: null,
      // tell Account Create Fields which fields are required, because this may differ from flow to flow
      requiredFields: ['firstName', 'lastName', 'email', 'password', 'zipcode', 'phone', 'gender', 'termsAccepted'],
      // if CreateSitter fails, alert user
      serverError: null,
      loading: false,
    };
  },
  mounted() {
    sendSegmentEvent('Registration Started', {
      registration_experience: 'direct',
      role: 'sitter',
    });
  },
  methods: {
    handleInput(key, value) {
      // generic input handler for child components that only emit one value
      this.form[key] = value;
    },
    handleAccountCreateInput(accountCreateFields) {
      // map values from account create fields to the parent form
      Object.keys(this.form)
        // check if parent form has corresponding key, or the rest of the values will be set to undefined
        .filter((key) => !!accountCreateFields[key])
        // if the key exists both in parent form and in account create fields, set parent form value
        .forEach((key) => {
          this.form[key] = accountCreateFields[key];
        });
      // account create fields gives us a zipcode but we need location coordinates
      if (accountCreateFields?.zipcode.length === 5) {
        this.getCoordinatesFromGoogle(accountCreateFields?.zipcode);
      }
    },
    async getCoordinatesFromGoogle(zipcode) {
      const results = await this.geoCode(zipcode);
      this.form.location = {
        x: results[0].geometry.location.lng(),
        y: results[0].geometry.location.lat(),
      };
      this.city = results[0].address_components[1].long_name;
      this.zipCode = results[0].address_components[0].long_name;
    },
    geoCode(zipcode): any {
      return new Promise<any>((resolve) => {
        new google.maps.Geocoder().geocode({ address: `${zipcode}, United States` }, (results, status) => {
          if (status === 'OK') {
            resolve(results);
          } else {
            console.error('Geocode was not successful', status, results); // eslint-disable-line no-console
          }
        });
      });
    },
    async validateAll() {
      // reset server error in case user is resubmitting
      this.resetServerError();
      // only submit if child components pass validation
      if (this.allChildrenAreValid()) {
        await this.createSitter();
      }
    },
    // validate all child components at once
    allChildrenAreValid() {
      this.$refs.accountCreateFields?.validateAll();
      this.$refs.phoneField?.validatePhone();
      this.$refs.birthDateFields?.validateAll();
      this.$refs.genderRadio?.validateGender();
      this.$refs.termsOfUse?.validate();

      return (
        this.$refs.accountCreateFields?.validateAll() &&
        this.$refs.phoneField?.validatePhone() &&
        this.$refs.birthDateFields?.validateAll() &&
        this.$refs.genderRadio?.validateGender() &&
        this.$refs.termsOfUse?.validate()
      );
    },
    async sitterRoleAssigned(sitterSso) {
      const { email, firstName, lastName } = this.form;

      if (window.dataLayer && email) {
        const emailSha1 = await generateSha1Hash(email);

        window.dataLayer.push({
          event: 'identify',
          email,
          emailSha1,
          customerID: sitterSso,
        });
      }

      await sendSegmentEventWithTimeout('Sitter Role Assigned', {
        user_id: sitterSso,
        event: 'Sitter Role Assigned',
        address: {
          city: this.city,
          country: 'USA',
          postalCode: this.zipCode,
        },
        corporateName: null,
        corporateSlug: null,
        createdAt: new Date(),
        email,
        familyType: null,
        firstName,
        isCorporate: false,
        isFirstResponder: false,
        isVolunteer: false,
        lastName,
        sitterType: 'basic',
      });
    },
    async linkInvitedSitterAndRedirect() {
      const invitedSitterToken = readDomainCookie('invitedSitterToken');
      const interviewRequestId = readDomainCookie('interviewRequestId');
      const invitedSitterId = readDomainCookie('invitedSitterId');

      if (invitedSitterToken && interviewRequestId && invitedSitterId) {
        try {
          await consumeInvitedSitterToken(interviewRequestId, invitedSitterToken, invitedSitterId);
          writeDomainCookie('skipProfileCreate', 'true');
        } catch (error) {
          console.error('Error consuming invited sitter token:', error);
        }
      }

      this.redirectToLocation();
      return true;
    },
    // call graphQL only if all child components validate
    async createSitter() {
      this.loading = true;
      try {
        const response = await this.$apollo.mutate({
          mutation: CREATE_SITTER,
          variables: {
            input: this.form,
          },
        });

        const { token, refreshToken } = response?.data?.account?.createSitter;
        const sitterSso = response?.data?.account?.createSitter?.account?.id;
        if (token && refreshToken) {
          writeDomainCookie('jwt', token);
          writeDomainCookie('refresh', refreshToken);
          window.sessionStorage.setItem('registrationID', sitterSso); // relevant to clearCache function in store

          // Populate the store
          try {
            await this.$store.dispatch('account/fetchAccountData');
          } catch (error) {
            console.error('Failed to fetch account data after account creation:', error);
          }

          await this.sitterRoleAssigned(sitterSso);
          await this.linkInvitedSitterAndRedirect();
        } else {
          this.loading = false;
          console.error('Expected a jwt and refresh token in the response, but received: ', response);
          this.serverError = new Error('missing-login-tokens');
        }
      } catch (error) {
        this.loading = false;
        if (error instanceof Error) {
          if (error.message === 'Underage user') {
            writeDomainCookie('ineligible', 'true');
            this.redirectToUnderagePage();
            return;
          }
          this.serverError = error;
        } else {
          console.error(error);
          this.serverError = new Error('unknown-error');
        }
      }
    },
    redirectToLocation() {
      this.$router.push({ path: '/sitter/signup/address' });
    },
    redirectToUnderagePage() {
      window.location.href = '/locked_out';
    },
    resetServerError() {
      this.serverError = null;
    },
  },
};
</script>
